import { AccountState } from '@/utils/account';
import { AppBundle } from '@/utils/app-bundles';
import { Box, Image, Link } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import customerIoLogo from '@/assets/images/logos/logomark-mint.svg';
import { useActiveBundle } from '@/context/active-bundle';
import type { NavigationContextHistoryState } from '@/context/navigation';
import { useNavigationContext } from '@/context/navigation';
import type { TargetTransitionOptions } from '@/context/safe-navigate';
import { useSafeNavigate } from '@/context/safe-navigate';
import { authStore, hasFeatureFlag } from '@/stores/auth';
import { shouldProcessLinkClick, useIsSettings, useMasqLink } from '@/utils/navigation';
import { PersonDropdown } from './components/person-dropdown';
import { SampleWorkspaceBadge } from './components/sample-workspace';
import { SettingsDropdown } from './components/settings-dropdown';
import { SupportChat } from './components/support-chat';
import { WorkspaceSelector } from './components/workspace-selector';
import * as styles from './index.css';
import { Copilot } from '@/features/copilot/components';
import { GlobalCopilotConfiguration } from '@/features/copilot/components/global-copilot-configuration';

function ExitSettingsLink() {
  const safeNavigate = useSafeNavigate();
  const masqLink = useMasqLink();
  const activeApp = useActiveBundle();
  const navigationContext = useNavigationContext();
  const location = useLocation();

  // Get the latest history entry right as the component is mounted.
  // The navigation context history is updated via an effect, so the current
  // route's info will not be available until the next render.
  // This works for us, because we only need to know the route we were on just
  // before visiting the settings routes anyway.
  const exitSettingsRouteInfo = useRef<NavigationContextHistoryState | null>(null);
  const lastHistoryEntry = navigationContext.history[navigationContext.history.length - 1];

  if (lastHistoryEntry?.url !== `${location.pathname}${location.search}` && exitSettingsRouteInfo.current == null) {
    exitSettingsRouteInfo.current = lastHistoryEntry;
  }

  if (exitSettingsRouteInfo.current == null) {
    exitSettingsRouteInfo.current = {
      url: '/',
      app: activeApp,
    };
  }

  const exitSettingsUrl = masqLink(exitSettingsRouteInfo.current?.url ?? '/');
  const onExitSettingsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const targetUrl = event.currentTarget.getAttribute('href') ?? '';
    const containsPipelines = targetUrl.includes(AppBundle.Pipelines);

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', {
      shouldUnrenderOutlet: containsPipelines || activeApp !== exitSettingsRouteInfo.current?.app,
    });
  };

  return (
    <Link
      href={exitSettingsUrl}
      onClick={(e: any) => onExitSettingsClick(e)}
      className={styles.exitLink}
      icon="chevron-left"
      variant="secondary"
      ml="200"
    >
      Exit Settings
    </Link>
  );
}

export function TopNavigation() {
  const auth = useStore(authStore);
  const safeNavigate = useSafeNavigate();

  const { isAnySettingsPage } = useIsSettings();

  const hasWorkspaceAccess =
    Object.keys(auth.user?.workspace_permissions ?? {}).length > 0 && // Don't show the link if they have no workspace access
    auth.account?.account_state !== AccountState.Suspended &&
    auth.account?.account_state !== AccountState.Canceled;

  const showExitSettingsLink =
    auth.account?.is_masq ?? false
      ? !(auth.user?.noMasqAccess ?? false) && !(auth.user?.masqLevelNotSet ?? false)
      : hasWorkspaceAccess;

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => {
    if (!shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', opts);
  };

  return (
    <div className={styles.topNav}>
      <Box display="flex" alignItems="center" gap="100">
        {isAnySettingsPage ? (
          showExitSettingsLink ? (
            <ExitSettingsLink />
          ) : null
        ) : (
          <>
            <div className={styles.logoContainer}>
              <Image src={customerIoLogo} alt="Customer.io logo" width={18} />
            </div>
            <Box ml="200">
              <WorkspaceSelector onLinkClick={onLinkClick} />
            </Box>
          </>
        )}
        <div className={styles.portal} id="hydra-top-nav-portal-left"></div>
      </Box>

      <SampleWorkspaceBadge />

      <Box display="flex" alignItems="center" gap="100">
        <div className={styles.portal} id="hydra-top-nav-portal-right"></div>
        <SupportChat />
        {hasFeatureFlag('ai-copilot') && hasFeatureFlag('ai_enabled') ? (
          <>
            <Copilot />
            <GlobalCopilotConfiguration />
          </>
        ) : null}
        <SettingsDropdown />
        <PersonDropdown />
      </Box>
    </div>
  );
}
